/* eslint-disable @typescript-eslint/no-unused-vars */

/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InstanceProps, create } from 'react-modal-promise';
import { Button, Col, Form, Modal, Row, notification } from 'antd';
import { AngleIcon, CloseIcon } from 'components/icons';
import { Select } from 'components/select';
import { useEmployee } from 'hooks';
import { ValidatorRule } from 'rc-field-form/lib/interface';
import { EpSkill } from 'types/graphql-types';
import * as query from './query';

type EditLanguageSkillsModalProps = InstanceProps<EpSkill[]>;

const emptyNode = <></>;

const EditLanguageSkillsModal: FC<EditLanguageSkillsModalProps> = ({
  onResolve,
  onReject,
  isOpen,
}) => {
  const { employee } = useEmployee();
  const langSkills = employee?.languages || [];

  const emsEnglishLevel = employee?.englishLevel;

  const { data: langData } = query.useLanguageList();
  const { data: levelData } = query.useLangLevelList();
  const { mutation: saveLanguageSkills, loading } =
    query.useSaveLanguageSkills();

  const { t } = useTranslation('translation');

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    langSkills.map((skill) => skill.name),
  );

  const allowedLanguageOptions = langData?.languageList
    .filter((lang) => !selectedLanguages.includes(lang))
    .map((lang) => ({
      label: lang,
      value: lang,
    }));

  const levelOptions = levelData?.langLevelList.map((level) => ({
    label: level,
    value: level,
  }));

  const initialValues = langSkills.map((skill) => ({
    language: skill.name,
    level: skill.level,
  }));

  (initialValues['ems-language' as unknown as number] as unknown) = 'English';

  const noEmptyValueRule: ValidatorRule = {
    validator: (_, value, callback) => {
      if (!value) {
        callback(t('Language is mandatory'));
      } else {
        callback();
      }
    },
  };

  const tryToClose = (): void => {
    Modal.confirm({
      cancelText: t('Continue'),
      content: t('Changes you made so far will not be saved'),
      okText: t('Yes, quit'),
      onOk: () => onReject(),
      title: t('Quit editing?'),
    });
  };

  const onSubmit = async (values: {
    languageSkills: { language: string; level: string }[];
  }): Promise<void> => {
    const skills = values.languageSkills.map((skill) => ({
      level: skill.level,
      name: skill.language,
    }));

    await saveLanguageSkills(skills)
      .then(() => {
        notification.success({
          message: t('Languages have been saved'),
        });
      })
      .catch(() => {
        notification.error({
          message: t('Something has gone wrong. Try again!'),
        });
      })
      .finally(onResolve);
  };

  return (
    <Modal
      open={isOpen}
      centered
      okText={t('Save')}
      onCancel={tryToClose}
      title={t('Edit languages')}
      closeIcon={<CloseIcon />}
      footer={[
        <Button key="cancel" onClick={tryToClose} loading={loading}>
          Cancel
        </Button>,
        <Button
          form="add-languages-form"
          key="save"
          htmlType="submit"
          type="primary"
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <Form
        name="add-languages-form"
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          'ems-language': 'English',
          'ems-languages-skill': emsEnglishLevel,
        }}
      >
        {emsEnglishLevel && (
          <Row gutter={[16, 0]} align="bottom">
            <Col span={24} sm={{ span: 12 }}>
              <Form.Item name={'ems-language'} label={<>{'Language'}</>}>
                <Select disabled={true} suffixIcon={emptyNode} />
              </Form.Item>
            </Col>
            <Col span={24} sm={{ span: 12 }}>
              <Form.Item name={'ems-languages-skill'} label={'Level'}>
                <Select
                  options={levelOptions}
                  disabled={true}
                  suffixIcon={<AngleIcon />}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Form.List name="languageSkills" initialValue={initialValues}>
          {(fields) => (
            <>
              {fields.map(({ key, name }) => (
                <Row key={key} gutter={[16, 0]} align="middle">
                  <Col span={24} sm={{ span: 12 }}>
                    <Form.Item
                      name={[name, 'language']}
                      label={'Language'}
                      rules={[noEmptyValueRule]}
                    >
                      <Select
                        showSearch={true}
                        onChange={(value) =>
                          setSelectedLanguages((oldSelectedLanguages) => {
                            const head = oldSelectedLanguages.slice(0, name);
                            const tail = oldSelectedLanguages.slice(name + 1);

                            return [...head, value as string, ...tail];
                          })
                        }
                        disabled={true}
                        placeholder="Select language"
                        options={allowedLanguageOptions}
                        suffixIcon={emptyNode}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} sm={{ span: 12 }}>
                    <Form.Item
                      name={[name, 'level']}
                      label={'Level'}
                      initialValue={initialValues[0]?.level}
                    >
                      <Select
                        options={levelOptions}
                        suffixIcon={<AngleIcon />}
                        value={initialValues[0]?.level}
                        placeholder="Select level"
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export const editLanguageSkillsModal = create(EditLanguageSkillsModal);
